import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Attach JWT from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nr_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Attempt silent token refresh on 401, then retry once
let _refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err?.response?.status !== 401 || original._retried) return Promise.reject(err);

    const refreshToken = localStorage.getItem('nr_refresh_token');
    if (!refreshToken) return Promise.reject(err);

    original._retried = true;

    // Deduplicate concurrent refresh calls
    if (!_refreshing) {
      _refreshing = api.post('/auth/refresh', { refresh_token: refreshToken })
        .then(({ data }) => {
          localStorage.setItem('nr_token', data.access_token);
          localStorage.setItem('nr_refresh_token', data.refresh_token);
          return data.access_token;
        })
        .catch((refreshErr) => {
          localStorage.removeItem('nr_token');
          localStorage.removeItem('nr_refresh_token');
          window.dispatchEvent(new Event('nr:logout'));
          return Promise.reject(refreshErr);
        })
        .finally(() => { _refreshing = null; });
    }

    try {
      const newToken = await _refreshing;
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch {
      return Promise.reject(err);
    }
  }
);

// --- Auth ---

export async function registerUser(email, password, playerTag) {
  const { data } = await api.post('/auth/register', { email, password, playerTag });
  return data; // { access_token, refresh_token, user }
}

export async function loginUser(email, password) {
  const { data } = await api.post('/auth/login', { email, password });
  return data; // { access_token, refresh_token, user }
}

export async function getMe() {
  const { data } = await api.get('/auth/me');
  return data; // user object
}

export async function claimProfile(shareableId) {
  const { data } = await api.post(`/auth/claim/${shareableId}`);
  return data; // updated user
}

export async function updateMyProfile(updates) {
  const { data } = await api.patch('/auth/profile', updates);
  return data; // updated user
}

export async function updateProfileTheme({ accentColor, cardStyle }) {
  const { data } = await api.patch('/auth/theme', { accentColor, cardStyle });
  return data; // updated user
}

export async function toggleBookmark(shareableId) {
  const { data } = await api.post(`/auth/bookmark/${shareableId}`);
  return data; // { bookmarks: [...] }
}

export async function markNotificationsRead() {
  const { data } = await api.patch('/auth/notifications/read');
  return data; // updated user
}

export async function getPlatforms() {
  const { data } = await api.get('/auth/platforms');
  return data; // { discord: bool, steam: bool, twitch: bool }
}

export async function getLinkToken() {
  const { data } = await api.post('/auth/link/token');
  return data; // { token }
}

export async function unlinkPlatform(platform) {
  const { data } = await api.delete(`/auth/platforms/${platform}`);
  return data; // updated user
}

export async function resendVerification() {
  const { data } = await api.post('/auth/resend-verification');
  return data;
}

export async function deleteAccount(password) {
  const { data } = await api.delete('/auth/account', { data: { password } });
  return data;
}

export async function forgotPassword(email) {
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword(token, password) {
  const { data } = await api.post('/auth/reset-password', { token, password });
  return data; // { access_token, refresh_token, user }
}

// --- Session ---

export async function startSession(playerTag, genre = 'general') {
  const ref = localStorage.getItem('nr_ref') || undefined;
  const { data } = await api.post('/session/start', { playerTag, genre, ref });
  return data; // { sessionId, playerTag }
}

export async function getTrialSequence(sessionId, module) {
  const { data } = await api.get(`/session/${sessionId}/trials/${module}`);
  return data; // { module, trials }
}

export async function submitModule(sessionId, module, trials) {
  const { data } = await api.post(`/session/${sessionId}/module`, { module, trials });
  return data; // { saved: true, module }
}

export async function completeSession(sessionId) {
  const { data } = await api.post(`/session/${sessionId}/complete`);
  return data; // { shareableId, scores, archetype }
}

// --- Profiles ---

export async function getProfile(shareableId) {
  const { data } = await api.get(`/profile/${shareableId}`);
  return data;
}

export async function getMatchup(shareableId, viewerArchetype = null) {
  const params = viewerArchetype ? `?viewerArchetype=${encodeURIComponent(viewerArchetype)}` : '';
  const { data } = await api.get(`/profile/${shareableId}/matchup${params}`);
  return data; // { matchup: string | null }
}

export async function getProfilesByTag(playerTag) {
  const { data } = await api.get(`/profiles/by-tag/${encodeURIComponent(playerTag)}`);
  return data; // { profiles }
}

// --- Practice ---

export async function getPracticeTrials(module) {
  const { data } = await api.get(`/practice/trials/${module}`);
  return data; // { module, trials }
}

// --- Scout ---

export async function searchProfiles(filters = {}) {
  const { data } = await api.get('/scout/profiles', { params: filters });
  return data; // { profiles, count }
}

// --- Leaderboard ---

export async function getLeaderboard(params = {}) {
  const { data } = await api.get('/leaderboard', { params });
  return data; // { entries }
}

// --- Stats ---

export async function getStats() {
  const { data } = await api.get('/stats');
  return data; // { profileCount }
}

export async function sendProfileLink(shareableId, email) {
  const { data } = await api.post(`/profile/${shareableId}/send-link`, { email });
  return data; // { ok: true }
}

export async function getRecentProfiles(limit = 8) {
  const { data } = await api.get('/profiles', { params: { limit } });
  return data; // [{ shareableId, playerTag, archetype, overallScore }]
}

// --- Challenge tracking ---

export async function trackChallengeEvent(event, challengerShareableId, acceptorShareableId) {
  api.post('/session/challenge-event', { event, challengerShareableId, acceptorShareableId }).catch(() => {});
}

// --- Admin ---

export async function adminAutoComplete(sessionId) {
  const { data } = await api.post(`/admin/session/${sessionId}/autocomplete`);
  return data; // { shareableId, scores, archetype }
}

// --- Billing ---

export async function createCheckout(type, opts = {}) {
  const { data } = await api.post('/billing/checkout', { type, ...opts });
  return data; // { url }
}

export async function createPortalSession() {
  const { data } = await api.post('/billing/portal');
  return data; // { url }
}

export async function getSubscription() {
  const { data } = await api.get('/billing/subscription');
  return data; // { subscription, retestCredits, featuredProfiles }
}

// --- Referral ---
export async function getReferralInfo() {
  const { data } = await api.get('/auth/referral');
  return data;
}
export async function applyReferral(code) {
  const { data } = await api.post('/auth/referral/apply', { code });
  return data;
}
export async function validateReferralCode(code) {
  const { data } = await api.get(`/referral/validate/${code}`);
  return data;
}

// --- Player Pro ---
export async function getRankComparison(shareableId) {
  const { data } = await api.get(`/profile/${shareableId}/rank-comparison`);
  return data;
}
export async function getDeepStats(shareableId) {
  const { data } = await api.get(`/profile/${shareableId}/deep-stats`);
  return data;
}
export async function getCoachingStatus(shareableId) {
  const { data } = await api.get(`/profile/${shareableId}/coaching`);
  return data;
}
export async function getTrainingPlan(shareableId) {
  const { data } = await api.get(`/profile/${shareableId}/training-plan`);
  return data;
}
export async function regenerateTrainingPlan(shareableId) {
  const { data } = await api.post(`/profile/${shareableId}/training-plan/regenerate`);
  return data;
}
export async function getCoachTwinProgress() {
  const { data } = await api.get('/coach-twin/progress');
  return data.progress;
}
